import type {
  AisleDefinition,
  AisleWalkthrough,
  CampaignPlacement,
  FloorplanZone,
  MediaLocation,
  StoreImage,
  StoreProfileDetail,
} from '../types';

/** A physical shape on the floorplan — never a retailer category name alone. */
export type SpatialEntityKind = 'aisle' | 'zone';

export interface SpatialEntity {
  kind: SpatialEntityKind;
  id: string;
  label: string;
  instoreLocationId?: number | null;
  x: number;
  y: number;
  width: number;
  height: number;
  centerX: number;
  centerY: number;
}

export interface EvidenceMappingStats {
  total: number;
  mapped: number;
  needsReview: number;
  unplaced: number;
}

function entityCenter(bounds: Pick<SpatialEntity, 'x' | 'y' | 'width' | 'height'>) {
  return {
    centerX: bounds.x + bounds.width / 2,
    centerY: bounds.y + bounds.height / 2,
  };
}

/** Canonical store areas drawn on the floorplan (aisles + zones). */
export function buildSpatialEntities(profile: Pick<StoreProfileDetail, 'aisles' | 'floorplanZones'>): SpatialEntity[] {
  const aisleEntities: SpatialEntity[] = profile.aisles.map((aisle) => ({
    kind: 'aisle',
    id: aisle.id,
    label: aisle.name || `Aisle ${aisle.number}`,
    x: aisle.x,
    y: aisle.y,
    width: aisle.width,
    height: aisle.height,
    ...entityCenter(aisle),
  }));

  const zoneEntities: SpatialEntity[] = profile.floorplanZones.map((zone) => ({
    kind: 'zone',
    id: zone.id,
    label: zone.label,
    instoreLocationId: zone.instoreLocationId,
    x: zone.x,
    y: zone.y,
    width: zone.width,
    height: zone.height,
    ...entityCenter(zone),
  }));

  return [...aisleEntities, ...zoneEntities];
}

export function countStoreAreas(profile: Pick<StoreProfileDetail, 'aisles' | 'floorplanZones'>): number {
  return profile.aisles.length + profile.floorplanZones.length;
}

export function findSpatialEntity(
  entities: SpatialEntity[],
  kind: SpatialEntityKind,
  id: string,
): SpatialEntity | undefined {
  return entities.find((entity) => entity.kind === kind && entity.id === id);
}

export function resolveSpatialEntityForZoneId(
  entities: SpatialEntity[],
  zoneId: string | null | undefined,
): SpatialEntity | undefined {
  if (!zoneId) return undefined;
  return findSpatialEntity(entities, 'zone', zoneId);
}

export function resolveSpatialEntityForAisleId(
  entities: SpatialEntity[],
  aisleId: string | null | undefined,
): SpatialEntity | undefined {
  if (!aisleId) return undefined;
  return findSpatialEntity(entities, 'aisle', aisleId);
}

export function resolveSpatialEntityForInstoreLocation(
  entities: SpatialEntity[],
  zones: FloorplanZone[],
  instoreLocationId: number | null | undefined,
): SpatialEntity | undefined {
  if (instoreLocationId == null) return undefined;
  const zone = zones.find((item) => item.instoreLocationId === instoreLocationId);
  if (!zone) return undefined;
  return resolveSpatialEntityForZoneId(entities, zone.id);
}

/** Resolve the one canonical spatial entity an image belongs to. */
export function resolveImageSpatialEntity(
  image: StoreImage,
  entities: SpatialEntity[],
  zones: FloorplanZone[],
): SpatialEntity | undefined {
  if (image.floorplanAisleId) {
    return resolveSpatialEntityForAisleId(entities, image.floorplanAisleId);
  }
  if (image.floorplanZoneId) {
    return resolveSpatialEntityForZoneId(entities, image.floorplanZoneId);
  }
  const hintId = image.instoreLocationIds?.[0] ?? image.instoreLocationId ?? image.postingInstructionInstoreLocationId;
  return resolveSpatialEntityForInstoreLocation(entities, zones, hintId);
}

/** Resolve the one canonical spatial entity a campaign placement belongs to. */
export function resolveCampaignSpatialEntity(
  campaign: CampaignPlacement,
  entities: SpatialEntity[],
  zones: FloorplanZone[],
  aisles: AisleDefinition[],
): SpatialEntity | undefined {
  if (campaign.floorplanZoneId) {
    return resolveSpatialEntityForZoneId(entities, campaign.floorplanZoneId);
  }
  const aisle = aisles.find((item) => item.id === campaign.aisleId);
  if (aisle) {
    return resolveSpatialEntityForAisleId(entities, aisle.id);
  }
  return resolveSpatialEntityForInstoreLocation(entities, zones, campaign.instoreLocationId);
}

/** Resolve the one canonical spatial entity a media location belongs to. */
export function resolveMediaSpatialEntity(
  media: MediaLocation,
  entities: SpatialEntity[],
  zones: FloorplanZone[],
): SpatialEntity | undefined {
  if (media.floorplanZoneId) {
    return resolveSpatialEntityForZoneId(entities, media.floorplanZoneId);
  }
  if (media.floorplanAisleId) {
    return resolveSpatialEntityForAisleId(entities, media.floorplanAisleId);
  }
  return undefined;
}

export function resolveWalkthroughSpatialEntities(
  walk: AisleWalkthrough,
  entities: SpatialEntity[],
  zones: FloorplanZone[],
): SpatialEntity[] {
  const resolved: SpatialEntity[] = [];
  for (const aisleId of walk.floorplanAisleIds ?? []) {
    const entity = resolveSpatialEntityForAisleId(entities, aisleId);
    if (entity) resolved.push(entity);
  }
  for (const locationId of walk.instoreLocationIds ?? []) {
    const entity = resolveSpatialEntityForInstoreLocation(entities, zones, locationId);
    if (entity && !resolved.some((item) => item.id === entity.id && item.kind === entity.kind)) {
      resolved.push(entity);
    }
  }
  return resolved;
}

/** Single source of truth for evidence mapping counts shown in Manage Twin. */
export function computeEvidenceMappingStats(profile: StoreProfileDetail): EvidenceMappingStats {
  const needsReview = profile.pendingSuggestions.length;
  const unplaced = profile.unmappedEvidence.length;
  const total = profile.walkthroughs.length + profile.images.length + profile.referenceImages.length;
  const mapped = Math.max(0, total - needsReview - unplaced);
  return { total, mapped, needsReview, unplaced };
}

export function entityMatchesSelection(
  entity: SpatialEntity,
  selection: { kind: SpatialEntityKind; id: string } | null,
): boolean {
  if (!selection) return false;
  return entity.kind === selection.kind && entity.id === selection.id;
}
